/**
 * src/services/daily.api.ts 의 짝.
 *
 * 하루는 `db.dailies` 의 한 행이고 업무 줄이 그 안에 있다. 저장은 보낸 줄로 통째로 맞춘다 —
 * 빠진 줄은 지운 것이다. 정규와 연장은 저장하지 않고 하루 합계와 기준 시간에서 계산한다.
 *
 * 쓸 수 있는 날짜는 오늘과 내일뿐이고 내일은 계획만 받는다. 마감이 지난 하루는 수정 요청으로
 * 고치고, 승인하는 그 순간에 줄에 반영한다.
 */
import { HttpResponse, http } from "msw";
import { shiftDate, today, weekDates, weekStartOf } from "@/lib/dates";
import type {
  AdminDailyCategory,
  CategoryInput,
  CategoryPatch,
  DailyCategory,
  DailyCommentInput,
  Daily,
  DailyEditRequest,
  DailyNote,
  DailyRow,
  DailySaveInput,
  EditDecisionInput,
  EditRequestInput,
  EditRequestRow,
  HoursLine,
  MemberHours,
  RowTarget,
  WeekCheck,
} from "@/types";
import type { MockDaily } from "../data/dailies";
import type { MockComment, MockImage } from "../data/dailyExtras";
import { db } from "../db";
import { fail, latency, requireAdmin, requireUser } from "../http";
import { photoFor } from "../data/dailyPhotos";

const BASE = "/api/v1";
const MAX_ROWS = 20;
const MAX_DAY_HOURS = 24;
let seq = 0;

const round = (value: number) => Math.round(value * 100) / 100;
const sum = (values: number[]) => round(values.reduce((a, b) => a + b, 0));

const isAdmin = (userId: string) => db.users.find((u) => u.id === userId)?.role === "admin";
/** 관리자와 HR — 근무시간 집계와 Daily 수정 결재. */
const isHr = (userId: string) => isAdmin(userId) || !!db.users.find((u) => u.id === userId)?.is_hr;

/** 설정의 하루 입력 마감 시각. 근무 규칙에 있다. */
const deadline = () => `${db.siteConfig.daily_deadline}:00`;

const standardHoursOf = (userId: string) =>
  db.users.find((u) => u.id === userId)?.standard_daily_hours ?? 8;

/** 응답에는 user_id 가 없다. */
const toResponse = (daily: MockDaily): Daily => {
  const { user_id: _userId, ...rest } = daily;
  return rest;
};

const nameOf = (target: Pick<DailyRow, "project_id" | "category_id">): RowTarget => ({
  project_id: target.project_id,
  project_name: db.projects.find((p) => p.id === target.project_id)?.name ?? null,
  category_id: target.category_id,
  category_name: db.dailyCategories.find((c) => c.id === target.category_id)?.name ?? null,
});

/** 하루 합계에서 정규와 연장을 다시 센다. 줄에는 저장하지 않는다. */
function recount(daily: MockDaily): MockDaily {
  const total = sum(daily.rows.map((row) => row.actual_hours));
  daily.total_hours = total;
  daily.regular_hours = Math.min(total, daily.standard_hours);
  daily.overtime_hours = round(Math.max(0, total - daily.standard_hours));
  return daily;
}

const toComment = ({ daily_table_id: _table, deleted_at: _deleted, ...comment }: MockComment) =>
  comment;
const toImage = ({ daily_table_id: _table, ...image }: MockImage) => image;

/** 이 사람이 그 Daily 의 코멘트를 보고 쓸 수 있는가 — 남의 Daily 는 관리자만, 본인은 늘. */
function commentScope(userId: string, daily: MockDaily): { read: boolean; write: boolean } {
  const allowed = daily.user_id === userId || isAdmin(userId);
  return { read: allowed, write: allowed };
}

const find = (userId: string, date: string) =>
  db.dailies.find((d) => d.user_id === userId && d.work_date === date);

const weekCheck = (userId: string, weekStart: string): WeekCheck => {
  const days = weekDates(weekStart).flatMap((date) => {
    const daily = find(userId, date);
    return daily && daily.status === "submitted" ? [daily] : [];
  });
  const regular = sum(days.map((d) => d.regular_hours));
  const overtime = sum(days.map((d) => d.overtime_hours));
  const limits = db.siteConfig;
  const total = round(regular + overtime);
  return {
    week_start: weekStart,
    regular,
    overtime,
    total,
    over_total_limit: total > limits.weekly_total_limit,
    over_overtime_limit: overtime > limits.weekly_overtime_limit,
    extra_hours: round(Math.max(0, total - limits.weekly_total_limit)),
  };
};

let catSeq = 0;
/** 꺼 둔 분류 — 목 DB 의 분류 행에는 is_active 칸이 없어 여기 따로 둔다. */
const inactiveCategories: Record<string, boolean> = {};

const byOrder = (a: DailyCategory, b: DailyCategory) =>
  (a.parent_id ?? "").localeCompare(b.parent_id ?? "") || a.sort_order - b.sort_order;

const siblings = (parentId: string | null) =>
  db.dailyCategories.filter((row) => row.parent_id === parentId).sort(byOrder);

const adminCategories = (): AdminDailyCategory[] =>
  [...db.dailyCategories]
    .sort(byOrder)
    .map((row) => ({ ...row, is_active: !inactiveCategories[row.id] }));

export const dailyHandlers = [
  http.get(`${BASE}/daily-categories/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = [...db.dailyCategories].sort(
      (a, b) => (a.parent_id ?? "").localeCompare(b.parent_id ?? "") || a.sort_order - b.sort_order
    );
    return HttpResponse.json({ results });
  }),

  /* ── 업무 분류 관리 ───────────────────────────────────────────────────
   *
   * 목 DB 의 분류에는 `is_active` 가 없다 — 지금까지 켠 것만 두었기 때문이다. 설정 화면은 꺼
   * 둔 것까지 받으므로 여기서 true 를 채워 내보낸다. 끄는 길은 PATCH 하나뿐이다. */

  http.get(`${BASE}/admin/daily-categories/`, async () => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    return HttpResponse.json({ results: adminCategories() });
  }),

  http.post(`${BASE}/admin/daily-categories/`, async ({ request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const input = (await request.json()) as CategoryInput;
    const name = input.name?.trim();
    if (!name) return fail(422, "분류 이름을 적어 주세요.");
    const parentId = input.parent_id ?? null;
    // 하위의 하위는 만들지 않는다 — 두 칸까지다.
    if (parentId && db.dailyCategories.find((row) => row.id === parentId)?.parent_id)
      return fail(422, "하위 분류 아래에는 더 만들 수 없어요.");
    if (siblings(parentId).some((row) => row.name === name))
      return fail(409, "같은 이름의 분류가 있어요.");
    const row = {
      id: `cat-new-${++catSeq}`,
      parent_id: parentId,
      name,
      sort_order: siblings(parentId).length + 1,
    };
    db.dailyCategories.push(row);
    return HttpResponse.json({ ...row, is_active: true }, { status: 201 });
  }),

  http.patch(`${BASE}/admin/daily-categories/:id/`, async ({ params, request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const row = db.dailyCategories.find((item) => item.id === params.id);
    if (!row) return fail(404, "없는 분류입니다.");
    const patch = (await request.json()) as CategoryPatch & { is_active?: boolean };
    if (patch.name !== undefined) {
      const name = patch.name.trim();
      if (!name) return fail(422, "분류 이름을 적어 주세요.");
      if (siblings(row.parent_id).some((item) => item.id !== row.id && item.name === name))
        return fail(409, "같은 이름의 분류가 있어요.");
      row.name = name;
    }
    if (patch.sort_order !== undefined) row.sort_order = patch.sort_order;
    if (patch.is_active !== undefined) inactiveCategories[row.id] = !patch.is_active;
    return HttpResponse.json({ ...row, is_active: !inactiveCategories[row.id] });
  }),

  http.put(`${BASE}/admin/daily-categories/order/`, async ({ request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const { parent_id, ids } = (await request.json()) as {
      parent_id: string | null;
      ids: string[];
    };
    const current = siblings(parent_id ?? null);
    // 그 부모 아래 분류를 전부 보내야 한다 — 그새 하나가 늘었으면 순서가 어긋난다.
    if (current.length !== ids.length || !current.every((row) => ids.includes(row.id)))
      return fail(409, "그새 분류가 바뀌었어요. 다시 불러와 주세요.");
    ids.forEach((id, index) => {
      const row = db.dailyCategories.find((item) => item.id === id);
      if (row) row.sort_order = index + 1;
    });
    return HttpResponse.json({ results: adminCategories() });
  }),

  http.delete(`${BASE}/admin/daily-categories/:id/`, async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const index = db.dailyCategories.findIndex((item) => item.id === params.id);
    if (index < 0) return fail(404, "없는 분류입니다.");
    if (db.dailyCategories.some((item) => item.parent_id === params.id))
      return fail(409, "하위 분류가 있는 분류는 지울 수 없어요.");
    if (db.dailies.some((daily) => daily.rows.some((row) => row.category_id === params.id)))
      return fail(409, "이미 쓰인 분류는 지울 수 없어요. 대신 꺼 주세요.");
    db.dailyCategories.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  http.get(`${BASE}/dailies/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const weekOf = new URL(request.url).searchParams.get("week_of");
    if (!weekOf) return fail(422, "주를 지정해 주세요.");
    const weekStart = weekStartOf(weekOf);
    const dates = weekDates(weekStart);
    const results = db.dailies
      .filter((d) => d.user_id === user.id && dates.includes(d.work_date))
      .sort((a, b) => a.work_date.localeCompare(b.work_date))
      .map(toResponse);
    // 주 합계는 임시저장도 센다 — 쓰는 사람에게는 이미 일한 시간이다.
    const inWeek = db.dailies.filter((d) => d.user_id === user.id && dates.includes(d.work_date));
    const regular = sum(inWeek.map((d) => d.regular_hours));
    const overtime = sum(inWeek.map((d) => d.overtime_hours));
    const total = round(regular + overtime);
    const threshold = db.siteConfig.weekly_total_limit;
    const extra = round(Math.max(0, total - threshold));
    // 상한을 넘긴 날 — 날짜순으로 누적해 처음 넘어간 날이다.
    let running = 0;
    const extraFrom =
      inWeek
        .slice()
        .sort((a, b) => a.work_date.localeCompare(b.work_date))
        .find((d) => {
          running += d.total_hours;
          return running > threshold;
        })?.work_date ?? null;
    return HttpResponse.json({
      results,
      week_summary: {
        regular,
        overtime,
        total,
        extra_threshold: threshold,
        extra_hours: extra,
        extra_from_date: extra > 0 ? extraFrom : null,
      },
      daily_deadline: deadline(),
    });
  }),

  http.put(`${BASE}/dailies/:workDate/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const date = String(params.workDate);
    const now = today();
    if (date !== now && date !== shiftDate(now, 1)) return fail(422, "오늘과 내일만 쓸 수 있어요.");
    const input = (await request.json()) as DailySaveInput;
    if (input.rows.length > MAX_ROWS) return fail(422, "줄이 너무 많아요.");
    if (
      date > now &&
      (input.status === "submitted" ||
        input.rows.some((row) => row.actual_hours > 0 || row.description))
    )
      return fail(422, "내일 날짜에는 계획만 쓸 수 있어요.");
    if (sum(input.rows.map((row) => row.actual_hours)) > MAX_DAY_HOURS)
      return fail(422, "하루 24시간을 넘을 수 없어요.");
    if (input.rows.some((row) => !row.project_id && !row.category_id))
      return fail(422, "프로젝트나 분류 중 하나는 골라야 해요.");
    if (new Date().toTimeString().slice(0, 8) >= deadline() && date === now)
      return fail(409, "오늘 입력이 마감됐어요.");

    const stamp = new Date().toISOString();
    let daily = find(user.id, date);
    if (!daily) {
      daily = {
        id: `daily-${user.id}-${date}`,
        user_id: user.id,
        work_date: date,
        week_start: weekStartOf(date),
        status: "draft",
        submitted_at: null,
        standard_hours: standardHoursOf(user.id),
        total_hours: 0,
        regular_hours: 0,
        overtime_hours: 0,
        rows: [],
        notes: [],
        note: "",
      };
      db.dailies.push(daily);
    }
    const synced = syncRows(daily, input.rows, stamp, input.plan_saved);
    if (synced) return synced;
    if (input.notes) daily.notes = cleanNotes(input.notes);
    if (input.note !== undefined) daily.note = input.note.trim();
    daily.status = input.status;
    daily.submitted_at = input.status === "submitted" ? stamp : null;
    return HttpResponse.json(toResponse(recount(daily)));
  }),

  /* ── 수정 요청 ──────────────────────────────────────────────────────── */

  http.get(`${BASE}/daily-edit-requests/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.dailyEditRequests
      .filter((r) => r.requester_id === user.id)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/dailies/:dailyId/edit-requests/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const daily = db.dailies.find((d) => d.id === params.dailyId);
    if (!daily || daily.user_id !== user.id) return fail(404, "없는 Daily 입니다.");
    const input = (await request.json()) as EditRequestInput;
    if (!input.reason?.trim()) return fail(422, "사유를 적어 주세요.");
    if (db.dailyEditRequests.some((r) => r.daily_table_id === daily.id && r.status === "pending"))
      return fail(409, "이 Daily 에 대기 중인 요청이 이미 있어요.");
    const made: DailyEditRequest = {
      id: `edit-request-${++seq}`,
      daily_table_id: daily.id,
      work_date: daily.work_date,
      requester_id: user.id,
      requester_name: user.name,
      reason: input.reason.trim(),
      before: daily.rows.map(toEditRow),
      after: input.rows,
      notes: input.notes ?? null,
      status: "pending",
      decider_id: null,
      decided_at: null,
      decision_note: "",
      applied_at: null,
      created_at: new Date().toISOString(),
    };
    db.dailyEditRequests.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.post(`${BASE}/daily-edit-requests/:id/withdraw/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.dailyEditRequests.find((r) => r.id === params.id);
    if (!found || found.requester_id !== user.id) return fail(404, "없는 요청입니다.");
    if (found.status !== "pending") return fail(409, "이미 처리된 요청이에요.");
    found.status = "withdrawn";
    return HttpResponse.json(found);
  }),

  http.get(`${BASE}/admin/daily-edit-requests/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    if (!isHr(user.id)) return fail(403, "이 기능을 쓸 권한이 없습니다.");
    const status = new URL(request.url).searchParams.get("status");
    const results = db.dailyEditRequests
      .filter((r) => !status || r.status === status)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/daily-edit-requests/:id/decision/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    if (!isHr(user.id)) return fail(403, "이 기능을 쓸 권한이 없습니다.");
    const found = db.dailyEditRequests.find((r) => r.id === params.id);
    if (!found) return fail(404, "없는 요청입니다.");
    if (found.status !== "pending") return fail(409, "이미 처리된 요청이에요.");
    const input = (await request.json()) as EditDecisionInput;
    if (!input.approve && !input.note?.trim()) return fail(422, "반려 사유를 적어 주세요.");
    const stamp = new Date().toISOString();
    found.status = input.approve ? "approved" : "rejected";
    found.decider_id = user.id;
    found.decided_at = stamp;
    found.decision_note = input.note?.trim() ?? "";
    if (input.approve) {
      const daily = db.dailies.find((d) => d.id === found.daily_table_id);
      if (daily) {
        // 줄 id 가 없던 옛 요청은 지금 줄을 자리로 짝지어 id 를 채운다 — 서버와 같다.
        const after = found.after.some((row) => row.id)
          ? found.after
          : found.after.map((row, position) => ({ ...row, id: daily.rows[position]?.id }));
        const synced = syncRows(daily, after, stamp, false);
        if (synced) return synced;
        if (found.notes) daily.notes = cleanNotes(found.notes);
        recount(daily);
      }
      found.applied_at = stamp;
    }
    return HttpResponse.json(found);
  }),

  /* ── 코멘트 ──────────────────────────────────────────────────────── */

  http.get(`${BASE}/dailies/:dailyId/comments/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const daily = db.dailies.find((row) => row.id === params.dailyId);
    if (!daily) return fail(404, "없는 Daily 입니다.");
    const scope = commentScope(user.id, daily);
    if (!scope.read) return fail(403, "볼 수 없는 Daily 입니다.");
    const results = db.dailyComments
      .filter((row) => row.daily_table_id === daily.id && !row.deleted_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .map(toComment);
    return HttpResponse.json({ results, can_write: scope.write });
  }),

  http.post(`${BASE}/dailies/:dailyId/comments/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const daily = db.dailies.find((row) => row.id === params.dailyId);
    if (!daily) return fail(404, "없는 Daily 입니다.");
    if (!commentScope(user.id, daily).write) return fail(403, "코멘트를 쓸 수 없어요.");
    const input = (await request.json()) as DailyCommentInput;
    if (!input.body?.trim()) return fail(422, "내용을 적어 주세요.");
    const made = {
      id: `daily-comment-new-${++seq}`,
      daily_table_id: daily.id,
      daily_row_id: input.daily_row_id ?? null,
      author_id: user.id,
      body: input.body.trim(),
      created_at: new Date().toISOString(),
      updated_at: null,
      deleted_at: null,
    };
    db.dailyComments.push(made);
    return HttpResponse.json(toComment(made), { status: 201 });
  }),

  http.patch(`${BASE}/daily-comments/:id/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.dailyComments.find((row) => row.id === params.id && !row.deleted_at);
    if (!found) return fail(404, "없는 코멘트입니다.");
    if (found.author_id !== user.id) return fail(403, "자기 코멘트만 고칠 수 있어요.");
    const { body } = (await request.json()) as { body: string };
    if (!body?.trim()) return fail(422, "내용을 적어 주세요.");
    found.body = body.trim();
    found.updated_at = new Date().toISOString();
    return HttpResponse.json(toComment(found));
  }),

  http.delete(`${BASE}/daily-comments/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.dailyComments.find((row) => row.id === params.id && !row.deleted_at);
    if (!found) return fail(404, "없는 코멘트입니다.");
    if (found.author_id !== user.id && !isAdmin(user.id))
      return fail(403, "자기 코멘트만 지울 수 있어요.");
    // 행은 지우지 않고 삭제 시각만 채운다 — 작성자와 시각이 남아야 한다.
    found.deleted_at = new Date().toISOString();
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 이미지 ──────────────────────────────────────────────────────── */

  http.get(`${BASE}/dailies/:dailyId/images/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const daily = db.dailies.find((row) => row.id === params.dailyId);
    if (!daily) return fail(404, "없는 Daily 입니다.");
    if (daily.user_id !== user.id && !isAdmin(user.id)) return fail(403, "볼 수 없어요.");
    const results = db.dailyImages
      .filter((row) => row.daily_table_id === daily.id)
      .sort((a, b) => a.position - b.position)
      .map(toImage);
    return HttpResponse.json({
      results,
      limit_per_project: db.siteConfig.daily_image_limit,
    });
  }),

  http.post(`${BASE}/dailies/:dailyId/images/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const daily = db.dailies.find((row) => row.id === params.dailyId);
    if (!daily) return fail(404, "없는 Daily 입니다.");
    if (daily.user_id !== user.id) return fail(403, "자기 Daily 에만 올릴 수 있어요.");
    const form = await request.formData();
    const file = form.get("file");
    if (!(file instanceof File)) return fail(422, "사진을 골라 주세요.");
    const projectId = (form.get("project_id") as string | null) ?? null;
    const rowId = (form.get("daily_row_id") as string | null) ?? null;
    // 서버처럼 그 Daily 의 줄이 아니면 422 다(DAILY_ROW_NOT_FOUND).
    if (rowId && !daily.rows.some((row) => row.id === rowId))
      return fail(422, "이 Daily 에 없는 업무 줄입니다. 새로고침한 뒤 다시 저장해 주세요.");
    // 장수 상한은 하루와 프로젝트 조합당이다 — 업무 줄당이 아니다.
    const used = db.dailyImages.filter(
      (row) => row.daily_table_id === daily.id && row.project_id === projectId
    ).length;
    if (used >= db.siteConfig.daily_image_limit) return fail(409, "장수가 가득 찼어요.");
    const made = {
      id: `daily-image-new-${++seq}`,
      daily_table_id: daily.id,
      project_id: projectId,
      daily_row_id: rowId,
      file_name: file.name,
      content_type: file.type,
      byte_size: file.size,
      position: used + 1,
      uploaded_by: user.id,
      created_at: new Date().toISOString(),
    };
    db.dailyImages.push(made);
    return HttpResponse.json(toImage(made), { status: 201 });
  }),

  /**
   * 파일 바이트를 둘 곳이 없어 사무소 작업 사진 다섯 장 중 하나를 돌려준다(사진 id 마다 늘 같은 것).
   * 실제 서버는 NAS 에서 읽는다.
   */
  http.get(`${BASE}/daily-images/:id/content/`, async ({ params }) => {
    const found = db.dailyImages.find((row) => row.id === params.id);
    if (!found) return fail(404, "없는 사진입니다.");
    const photo = await fetch(photoFor(found.id));
    return new HttpResponse(await photo.arrayBuffer(), {
      headers: { "Content-Type": "image/jpeg" },
    });
  }),

  http.delete(`${BASE}/daily-images/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const index = db.dailyImages.findIndex((row) => row.id === params.id);
    if (index < 0) return fail(404, "없는 사진입니다.");
    const daily = db.dailies.find((row) => row.id === db.dailyImages[index].daily_table_id);
    if (daily && daily.user_id !== user.id && !isAdmin(user.id))
      return fail(403, "자기 사진만 지울 수 있어요.");
    db.dailyImages.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 근무시간 ──────────────────────────────────────────────────────── */
  http.get(`${BASE}/admin/working-hours/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = Number(query.get("year"));
    const month = Number(query.get("month"));
    const basis = (query.get("basis") ?? "project") as "project" | "user";
    const projectId = query.get("project_id");
    const userId = query.get("user_id");
    const weekStarts = monthWeeks(year, month);
    const scoped = submittedIn(year, month)
      .filter((d) => !userId || d.user_id === userId)
      .map((daily) => ({
        ...daily,
        rows: daily.rows.filter((row) => !projectId || row.project_id === projectId),
      }));

    const rows: MemberHours[] = [];
    for (const person of new Set(scoped.map((d) => d.user_id))) {
      const theirs = scoped.filter((d) => d.user_id === person);
      for (const line of groupLines(theirs, weekStarts, year, (row) => row))
        rows.push({
          ...line.target,
          user_id: person,
          user_name: db.users.find((u) => u.id === person)?.name ?? person,
          hours: line.hours,
        });
    }
    const people = [...new Set(scoped.map((d) => d.user_id))].map((person) => ({
      user_id: person,
      user_name: db.users.find((u) => u.id === person)?.name ?? person,
      weeks: weekStarts.map((weekStart) => weekCheck(person, weekStart)),
    }));
    const monthTotal = sum(scoped.flatMap((d) => d.rows.map((row) => row.actual_hours)));
    const previous = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
    const previousTotal = sum(
      submittedIn(previous.year, previous.month).flatMap((d) =>
        d.rows.map((row) => row.actual_hours)
      )
    );
    const regular = sum(people.flatMap((p) => p.weeks.map((w) => w.regular)));
    const overtime = sum(people.flatMap((p) => p.weeks.map((w) => w.overtime)));
    return HttpResponse.json({
      year,
      month,
      basis,
      week_starts: weekStarts,
      rows: basis === "user" ? rows : rows.sort(byTarget),
      summary: {
        month_total: monthTotal,
        previous_month_total: previousTotal,
        overtime_ratio: regular + overtime ? round(overtime / (regular + overtime)) : null,
      },
      people,
    });
  }),
];

const toEditRow = (row: DailyRow): EditRequestRow => ({
  id: row.id,
  project_id: row.project_id,
  category_id: row.category_id,
  plan_description: row.plan_description,
  plan_hours: row.plan_hours,
  description: row.description,
  actual_hours: row.actual_hours,
  done: !!row.completed_at,
});

/** 써 넣을 줄 하나 — 저장 줄과 수정 요청 줄이 함께 쓴다. 계획·끝냄이 없으면 그 칸을 건드리지 않는다. */
interface RowSpec {
  id?: string | null;
  project_id: string | null;
  category_id: string | null;
  plan_description?: string;
  plan_hours?: number;
  description: string;
  actual_hours: number;
  done?: boolean;
}

/**
 * 줄을 id 로 맞춘다 — 서버(app/dailies/rows.py)와 같다. id 를 실은 줄은 그 줄을 고치고, id 없이 온 줄은 새
 * 줄이며, 보내지 않은 줄은 지운다. 끝냄은 처음 체크한 시각을 지키고 체크를 풀면 비운다. 모르는 id 면
 * 422 응답을 돌려준다.
 */
function syncRows(daily: MockDaily, specs: RowSpec[], stamp: string, planSaved: boolean) {
  const existing = new Map(daily.rows.map((row) => [row.id, row]));
  if (specs.some((spec) => spec.id && !existing.has(spec.id)))
    return fail(422, "이 Daily 에 없는 업무 줄입니다. 새로고침한 뒤 다시 저장해 주세요.");
  daily.rows = specs.map((spec, position) => {
    const before = spec.id ? existing.get(spec.id) : undefined;
    const planDescription = spec.plan_description ?? before?.plan_description ?? "";
    const planHours = spec.plan_hours ?? before?.plan_hours ?? 0;
    const planChanged =
      !!before && (before.plan_description !== planDescription || before.plan_hours !== planHours);
    const done = spec.done ?? !!before?.completed_at;
    return {
      id: before?.id ?? `${daily.id}-r${position}-${++seq}`,
      position,
      ...nameOf(spec),
      plan_description: planDescription,
      plan_hours: planHours,
      plan_saved_at: before?.plan_saved_at ?? (planSaved ? stamp : null),
      plan_updated_at:
        before?.plan_saved_at && planChanged ? stamp : (before?.plan_updated_at ?? null),
      description: spec.description,
      actual_hours: spec.actual_hours,
      completed_at: done ? (before?.completed_at ?? stamp) : null,
    };
  });
  // 지운 줄에 붙었던 사진은 서버처럼 줄 연결만 비우고 하루 · 프로젝트에 남는다.
  const kept = new Set(daily.rows.map((row) => row.id));
  for (const image of db.dailyImages)
    if (image.daily_table_id === daily.id && image.daily_row_id && !kept.has(image.daily_row_id))
      image.daily_row_id = null;
  return null;
}

/** 카드 비고 — 빈 비고는 지운 것이고, 한 카드(프로젝트)에 하나뿐이다. */
const cleanNotes = (notes: DailyNote[]): DailyNote[] =>
  notes
    .map((note) => ({ project_id: note.project_id, body: note.body.trim() }))
    .filter(
      (note, index, all) =>
        note.body && all.findIndex((other) => other.project_id === note.project_id) === index
    );

const byTarget = (a: RowTarget, b: RowTarget) =>
  (a.project_name ?? "￿").localeCompare(b.project_name ?? "￿", "ko") ||
  (a.category_name ?? "").localeCompare(b.category_name ?? "", "ko");

/** 그 달에 걸치는 주의 월요일들. */
function monthWeeks(year: number, month: number): string[] {
  const first = `${year}-${String(month).padStart(2, "0")}-01`;
  const last = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
  const weeks: string[] = [];
  for (let week = weekStartOf(first); week <= last; week = shiftDate(week, 7)) weeks.push(week);
  return weeks;
}

const submittedIn = (year: number, month: number) => {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  return db.dailies.filter((d) => d.status === "submitted" && d.work_date.startsWith(prefix));
};

/** 대상(프로젝트·분류)마다 주별·월·연 누적을 모은다. */
function groupLines(
  dailies: MockDaily[],
  weekStarts: string[],
  year: number,
  pick: (row: DailyRow) => DailyRow
): { target: RowTarget; hours: HoursLine }[] {
  const lines = new Map<string, { target: RowTarget; hours: HoursLine }>();
  for (const daily of dailies)
    for (const row of daily.rows.map(pick)) {
      const key = `${row.project_id ?? ""}:${row.category_id ?? ""}`;
      const line =
        lines.get(key) ??
        (() => {
          const made = {
            target: nameOf(row),
            hours: { weeks: weekStarts.map(() => 0), month: 0, year_to_date: 0 },
          };
          lines.set(key, made);
          return made;
        })();
      const index = weekStarts.indexOf(daily.week_start);
      if (index >= 0) line.hours.weeks[index] = round(line.hours.weeks[index] + row.actual_hours);
      line.hours.month = round(line.hours.month + row.actual_hours);
    }
  // 연간 누적은 그해 전체에서 다시 센다 — 그 달 집계만으로는 낼 수 없다.
  for (const [key, line] of lines) {
    const [projectId, categoryId] = key.split(":");
    line.hours.year_to_date = sum(
      db.dailies
        .filter((d) => d.status === "submitted" && d.work_date.startsWith(String(year)))
        .filter((d) => dailies.some((mine) => mine.user_id === d.user_id))
        .flatMap((d) =>
          d.rows
            .filter(
              (row) =>
                (row.project_id ?? "") === projectId && (row.category_id ?? "") === categoryId
            )
            .map((row) => row.actual_hours)
        )
    );
  }
  return [...lines.values()];
}
